import type { CodexServerRequestHandlers } from "../codex-app-server.js";
import { isNativeApprovalPolicyActive, type NativeApprovalPolicy } from "./native-policy.js";
import type { CodexQuestions } from "./questions.js";

type Handler = NonNullable<CodexServerRequestHandlers["mcpServer/elicitation/request"]>;
const record = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object" && !Array.isArray(value);
const text = (value: unknown): value is string =>
  typeof value === "string" && value.length > 0 && Buffer.byteLength(value) <= 8192;

/** Human consent for the configured runner callback, independent of downstream tool authorization. */
export function createCodexMcpApprovalHandler(options: {
  serverName: string | undefined;
  questions: CodexQuestions;
  getPolicy: () => NativeApprovalPolicy | undefined;
  bindingActive: () => boolean;
}): Handler {
  return async (input, signal) => {
    const decline = { action: "decline" as const, content: null, _meta: null };
    try {
      const params = structuredClone(input);
      const active = () =>
        !signal.aborted &&
        options.bindingActive() &&
        isNativeApprovalPolicyActive(options.getPolicy(), params.threadId);
      if (
        !active() ||
        !text(options.serverName) ||
        params.serverName !== options.serverName ||
        !text(params.threadId) ||
        !text(params.turnId) ||
        params.mode !== "form" ||
        !text(params.message) ||
        Object.keys(params).some(
          (key) =>
            ![
              "threadId",
              "turnId",
              "serverName",
              "mode",
              "message",
              "requestedSchema",
              "_meta",
            ].includes(key),
        ) ||
        !record(params._meta) ||
        params._meta.codex_approval_kind !== "mcp_tool_call" ||
        !record(params.requestedSchema) ||
        params.requestedSchema.type !== "object" ||
        !record(params.requestedSchema.properties) ||
        Object.keys(params.requestedSchema.properties).length !== 0 ||
        Object.keys(params.requestedSchema).some(
          (key) => !["type", "properties", "required"].includes(key),
        ) ||
        (params.requestedSchema.required !== undefined &&
          (!Array.isArray(params.requestedSchema.required) ||
            params.requestedSchema.required.length !== 0)) ||
        Buffer.byteLength(JSON.stringify(params._meta)) > 65536
      )
        return decline;
      const accepted = await options.questions.requestApproval(
        undefined,
        {
          kind: "mcp",
          details: [
            `Configured MCP server: ${options.serverName}`,
            params.message,
            "This is native request presentation, not an independently verified tool identity. Tool permissions are checked again when dispatched.",
          ],
        },
        signal,
      );
      return accepted && active() ? { action: "accept", content: null, _meta: null } : decline;
    } catch {
      return decline;
    }
  };
}
